import { useEffect, useState } from "react";
import type * as React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ErasePhoneIcon from "@mui/icons-material/PhonelinkErase";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import PageTitle from "utils/PageTitle";
import Translate from "translate/Translate";
import {
  trackAccountMfaCompleted,
  trackAccountMfaFailed
} from "analytics";
import ModalWrapper from "components/v2/sharedComponents/Modals/ModalComponents/ModalWrapper";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { PATHS } from "constants/pathConstants";
import { removeCookie } from "utils/v3/CookieUtil";
import {
  disableMfa,
  enableMfa,
  getMemberMfaEnforcedStatus
} from "apis/v3/authApi";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import GLButton from "components/v3/sharedComponents/GLButton";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import { COLORS } from "components/v3/Theme/colors";

const PAGE_TITLE = "Multi-Factor Authentication";
const PAGE_HEADING = "Multi-Factor Authentication";

const mfaCardSx = {
  backgroundColor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  maxWidth: "100%",
  minWidth: 0,
  padding: 2.5,
  width: "100%"
} as const;

const primaryButtonTypographySx = {
  typography: { xs: "body_strong_white", md: "body_strong_lg_white" }
} as const;

const actionButtonSx = {
  alignSelf: "stretch",
  gap: "10px",
  justifyContent: "center",
  maxWidth: "100%",
  minHeight: "auto",
  mt: 3,
  px: 2,
  py: 1.5,
  width: "100%",
  "& > .MuiBox-root:first-of-type": {
    marginRight: "0 !important"
  },
  "& .MuiTypography-root": {
    flex: "0 0 auto",
    lineHeight: 1.35,
    textAlign: "left",
    whiteSpace: "normal"
  }
} as const;

const MFAPage: React.FC = () => {
  const dispatch = useDispatch();
  const { loginWithRedirect } = useAuth0();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isLoadingMfaStatus, setIsLoadingMfaStatus] = useState(true);
  const [isEnablingMfa, setIsEnablingMfa] = useState(false);
  const [isDisablingMfa, setIsDisablingMfa] = useState(false);
  const [reauthDialogIsOpen, setReauthDialogIsOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadMfaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  PageTitle(PAGE_TITLE);

  const loadMfaStatus = async () => {
    try {
      setIsLoadingMfaStatus(true);
      const response = await getMemberMfaEnforcedStatus();
      if (response && !axios.isAxiosError(response)) {
        const newMfaStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : false;
        setMfaEnabled(newMfaStatus);
        return newMfaStatus;
      }
      return false;
    } catch (error) {
      trackAccountMfaFailed("load_status", error);
      handleAPICallError();
      return false;
    } finally {
      setIsLoadingMfaStatus(false);
    }
  };

  const handleEnableMfa = () => {
    setReauthDialogIsOpen(true);
  };

  const handleEnableMfaAndReauth = async () => {
    try {
      setIsEnablingMfa(true);
      const response = await enableMfa();
      if (response && !axios.isAxiosError(response)) {
        const newStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : true;
        setMfaEnabled(newStatus);
        trackAccountMfaCompleted("enable");
      } else {
        throw new Error("Failed to enable MFA");
      }

      setReauthDialogIsOpen(false);

      removeCookie("accessToken");

      loginWithRedirect({
        authorizationParams: {
          prompt: "login",
          max_age: 0
        }
      });
    } catch (error) {
      trackAccountMfaFailed("enable", error);
      handleAPICallError();
    } finally {
      setIsEnablingMfa(false);
    }
  };

  const handleDisableMfa = async () => {
    try {
      setIsDisablingMfa(true);
      const response = await disableMfa();

      if (response && !axios.isAxiosError(response)) {
        trackAccountMfaCompleted("disable");
        const newStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : false;
        setMfaEnabled(newStatus);
        dispatch(
          openGLSnackbar({
            variant: "success",
            header: "MFA Disabled",
            subText:
              "Multi-factor authentication has been disabled for your account."
          })
        );
      } else {
        throw new Error("Failed to disable MFA");
      }
    } catch (error) {
      trackAccountMfaFailed("disable", error);
      handleAPICallError();
    } finally {
      setIsDisablingMfa(false);
    }
  };

  const handleAPICallError = () => {
    dispatch(
      openGLSnackbar({
        variant: "error",
        subText:
          "We're so sorry, but something went wrong. Please reach out to us!"
      })
    );
  };

  const resetReauthDialogState = () => {
    setReauthDialogIsOpen(false);
  };

  const modalSubtextSx = {
    color: COLORS.DARK_GRAY_2,
    fontSize: "1rem",
    fontStyle: "italic",
    lineHeight: 1.5,
    textAlign: "left" as const
  };

  const faqSectionTitleSx = {
    color: COLORS.BLACK,
    fontSize: "1rem",
    fontWeight: 700,
    mb: 1,
    mt: 5,
    textAlign: "center" as const
  };

  const faqBodySx = {
    color: COLORS.DARK_GRAY,
    fontSize: "1rem",
    fontStyle: "italic" as const,
    lineHeight: 1.65,
    textAlign: "center" as const
  };

  const loadingCard = (
    <Box sx={mfaCardSx}>
      <Typography
        variant="body_strong_black"
        sx={{
          color: COLORS.BLACK,
          fontSize: "1.125rem",
          fontWeight: 700,
          textAlign: "center",
          width: "100%"
        }}
      >
        <Translate text="Loading..." />
      </Typography>
    </Box>
  );

  const mainCard = (
    <Box sx={mfaCardSx}>
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Typography
          variant="body_strong_black"
          sx={{
            color: COLORS.BLACK,
            fontSize: "1.125rem",
            fontWeight: 700,
            mb: 2
          }}
        >
          <Translate text="Additional security measures" />
        </Typography>
        <Typography variant="body2" sx={faqBodySx}>
          <Translate
            text="Multi-factor Authentication (MFA) is an additional step at login to help secure your
        personal information."
          />
        </Typography>
      </Box>

      <Divider sx={{ borderColor: alpha(COLORS.DARK_GRAY, 0.25), my: 2.5 }} />

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          textAlign: "center",
          width: "100%"
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: mfaEnabled ? COLORS.SUCCESS : COLORS.NOTICE,
            fontSize: "0.75rem",
            fontWeight: 600,
            textAlign: "center"
          }}
        >
          <Translate
            text={`Text message verification is turned ${
              mfaEnabled ? "ON" : "OFF"
            }`}
          />
        </Typography>

        {mfaEnabled ? (
          <GLButton
            buttonType="primary"
            leftIcon={
              <ErasePhoneIcon
                sx={{ color: COLORS.WHITE, fontSize: "1.25rem" }}
              />
            }
            loading={isDisablingMfa}
            onClick={handleDisableMfa}
            sx={actionButtonSx}
          >
            <Typography sx={primaryButtonTypographySx}>
              <Translate text="Turn text authentication off" />
            </Typography>
          </GLButton>
        ) : (
          <GLButton
            buttonType="primary"
            leftIcon={
              <PhoneIphoneIcon
                sx={{ color: COLORS.WHITE, fontSize: "1.25rem" }}
              />
            }
            onClick={handleEnableMfa}
            sx={actionButtonSx}
          >
            <Typography sx={primaryButtonTypographySx}>
              <Translate text="Turn text authentication on" />
            </Typography>
          </GLButton>
        )}

        <Typography variant="body_strong_black" component="h2" sx={faqSectionTitleSx}>
          <Translate text="What is it?" />
        </Typography>
        <Typography variant="body2" sx={faqBodySx}>
          <Translate
            text="It is a multi-step account login process that requires users to 
          enter more information than just a password. It prevents bad-actors from accessing 
          your account even if they've acquired your log in credentials."
          />
        </Typography>

        <Typography variant="body_strong_black" component="h2" sx={faqSectionTitleSx}>
          <Translate text="What do I need to do?" />
        </Typography>
        <Typography variant="body2" sx={faqBodySx}>
          <Translate text="To enable MFA, click the button above. You will be prompted to log in again and enter a phone number where you can receive text messages." />
        </Typography>

        <Typography variant="body_strong_black" component="h2" sx={faqSectionTitleSx}>
          <Translate text="What if I want to turn it off/on?" />
        </Typography>
        <Typography variant="body2" sx={faqBodySx}>
          <Translate text="You can come back to this page to turn this on or off at any time." />
        </Typography>
      </Box>
    </Box>
  );

  return (
    <GradientFramedLayout showHeader={true}>
      <ModalWrapper
        closeButton={false}
        closeOnBackdropClick={true}
        isOpen={reauthDialogIsOpen}
        onClose={resetReauthDialogState}
        modalContainerSx={{
          flexBasis: {
            xs: "90% !important"
          },
          maxHeight: "85vh",
          maxWidth: {
            xs: "95%",
            lg: "805px !important"
          },
          overflow: "auto",
          padding: {
            xs: "32px 16px 24px 16px !important",
            lg: "40px 32px 32px 32px !important"
          },
          margin: "auto",
          outline: 0
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%"
          }}
        >
          <Typography
            variant="body_strong_black"
            sx={{
              color: COLORS.BLACK,
              fontSize: "1.125rem",
              fontWeight: 700,
              mb: 1,
              textAlign: "left"
            }}
          >
            <Translate text="Text message verification" />
          </Typography>

          <Typography variant="body2" sx={modalSubtextSx}>
            <Translate text="Please note: to finish enabling MFA, you’ll be asked to log in again." />
          </Typography>
          <Typography variant="body2" sx={modalSubtextSx}>
            <Translate text="During that login, you’ll be prompted to enter a valid phone number. We only allow US numbers at this time. This will be the phone number we send codes to, so please keep this phone handy now and any time you log in." />
          </Typography>
          <Typography variant="body2" sx={modalSubtextSx}>
            <Translate text="When you press Continue, you’ll be redirected to complete setup and may receive a verification code via text message." />
          </Typography>

          <Typography
            variant="body_strong_black"
            sx={{
              color: COLORS.BLACK,
              fontSize: "1rem",
              fontWeight: 700,
              marginTop: 1,
              textAlign: "left"
            }}
          >
            <Translate text="What do we need this phone number for?" />
          </Typography>
          <Typography variant="body2" sx={modalSubtextSx}>
            <Translate text="We will only use this phone number to authenticate your identity when you log in. We will not use this phone number for anything except authentication unless you also have the same phone number set as your contact phone number under your account settings. Changing that number will not change this number." />
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: { xs: 1, sm: 2 },
              justifyContent: "flex-end",
              marginTop: 2
            }}
          >
            <GLButton
              buttonType="secondary"
              disabled={isEnablingMfa}
              onClick={resetReauthDialogState}
              sx={{
                flex: "1 1 0",
                maxWidth: "none",
                width: "100%"
              }}
            >
              <Typography variant="body_strong_primary">
                <Translate text="Cancel" />
              </Typography>
            </GLButton>
            <GLButton
              buttonType="primary"
              disabled={isEnablingMfa}
              loading={isEnablingMfa}
              onClick={handleEnableMfaAndReauth}
              sx={{
                flex: "1 1 0",
                maxWidth: "none",
                width: "100%"
              }}
            >
              <Typography variant="body_strong_white">
                <Translate text="Continue" />
              </Typography>
            </GLButton>
          </Box>
        </Box>
      </ModalWrapper>

      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          width: "100%"
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: 500,
            minWidth: 0,
            paddingBottom: "40px",
            paddingTop: { md: 3, xs: 2 },
            paddingX: { md: 3, xs: 2 },
            width: "100%"
          }}
        >
          <Typography
            variant="h4_primary"
            component="h1"
            sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
          >
            <Translate text={PAGE_HEADING} />
          </Typography>

          <BreadcrumbTrail
            segments={[
              { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
              {
                label: <Translate text="Privacy & Security" />,
                to: PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY
              },
              { label: <Translate text={PAGE_HEADING} /> }
            ]}
          />

          {isLoadingMfaStatus ? loadingCard : mainCard}

          <Box
            sx={{
              flexShrink: 0,
              marginTop: "auto",
              width: "100%"
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default MFAPage;
